import React, { useCallback, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import { decipherError } from '../../../../../../utils/decipherError'
import { actionTemplates } from '../../../../../../api/coreData'

const useSubmitter = (form, onComplete) => {
  const { handleSubmit } = form
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: createActionTemplate } = actionTemplates.useCreateActionTemplate()
  const onSubmit = useCallback(async (formData) => {
    const command = {
      code: formData.code,
      name: formData.name,
      description: formData.description || null
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await createActionTemplate(command)
      if (result?.statusCode >= 400) {
        setError(result?.message || 'Something unexpected happened while creating the action template')
        return
      }
      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [setProcessing, onComplete, setError, createActionTemplate])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = () => {}

    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, onSubmit])

  return {
    submitter,
    processing,
    error
  }
}

function MapActionForm ({ onCancel, onComplete }) {
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      code: '',
      name: '',
      description: ''
    }
  })

  const { submitter, processing, error } = useSubmitter(form, onComplete)

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Controller
          name='code'
          control={form.control}
          rules={{
            required: { value: true, message: 'Required.' },
            maxLength: { value: 64, message: 'Max 64 Characters.' },
            pattern: { value: /^[a-zA-Z0-9_-]+$/, message: 'Only letters, numbers, hyphens, and underscores.' }
          }}
          render={(f) => (
            <SydLabel label='Code' description='Unique identifier for this action template' error={hookFormErrorAdapter(f, f.fieldState)} required>
              <SydInput {...f.field} autoFocus />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name='name'
          control={form.control}
          rules={{
            required: { value: true, message: 'Required.' },
            maxLength: { value: 128, message: 'Max 128 Characters.' }
          }}
          render={(f) => (
            <SydLabel label='Name' description='Display name for this action template' error={hookFormErrorAdapter(f, f.fieldState)} required>
              <SydInput {...f.field} />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name='description'
          control={form.control}
          rules={{
            maxLength: { value: 500, message: 'Max 500 Characters.' }
          }}
          render={(f) => (
            <SydLabel label='Description' error={hookFormErrorAdapter(f, f.fieldState)}>
              <SydInput {...f.field} multiline rows={3} />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <SydModalActions>
          {error ? <div className='__error'>{error}</div> : null}
          <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>Cancel</SydButton>
          <SydButton
            processing={processing}
            variant='primary'
            size='md'
            onClick={submitter}
            disabled={!form.formState.isValid}
          >
            Create
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

MapActionForm.propTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

export default MapActionForm
